// chatService.js
import apiClient from "./apiClient"; // axios import not needed here
import { fetchWithAuthRetry } from "./authService";

const getApiBaseUrl = () =>
  String(
    typeof apiClient?.defaults?.baseURL === "string"
      ? apiClient.defaults.baseURL
      : "",
  ).replace(/\/+$/, "");

const buildJsonRequestHeaders = () => {
  const tenantId = sessionStorage.getItem("tenantId");

  return {
    "Content-Type": "application/json",
    ...(tenantId ? { tenantId } : {}),
  };
};

const parseResponseBody = async (response) => {
  const contentType = (
    response.headers.get("content-type") || ""
  ).toLowerCase();

  if (contentType.includes("application/json")) {
    return response.json();
  }

  const text = await response.text();
  return text ? { message: text } : null;
};

const getRequestReferrer = () =>
  typeof window !== "undefined"
    ? window.location.origin.replace(/\/+$/, "")
    : undefined;

const postJsonWithReferrer = async (
  path,
  data,
  { referrerPolicy = "origin" } = {},
) => {
  const base = getApiBaseUrl();
  const url = `${base}${path}`;
  const referrer = getRequestReferrer();

  const response = await fetchWithAuthRetry(url, {
    method: "POST",
    credentials: "include",
    headers: buildJsonRequestHeaders(),
    body: JSON.stringify(data),
    ...(referrer
      ? {
          referrer,
          referrerPolicy,
        }
      : {}),
  });

  const responseData = await parseResponseBody(response);

  if (!response.ok) {
    const requestError = new Error(
      responseData?.message || `Request failed with status ${response.status}`,
    );
    requestError.status = response.status;
    requestError.response = {
      status: response.status,
      data: responseData,
    };
    throw requestError;
  }

  return responseData;
};

export async function streamPromptResponse(
  { message },
  { onChunk, signal, delayMs = 0, jsonField = null } = {},
) {
  if (!message) throw new Error('streamPromptResponse: "message" is required');

  const tenantId = sessionStorage.getItem("tenantId");
  if (!tenantId) throw new Error("streamPromptResponse: tenantId is required");

  const base = String(apiClient?.defaults?.baseURL || "").replace(/\/+$/, "");
  const url = `${base}/api/v3/ai/stream`;
  console.log("streamPromptResponse - URL:", url);

  const headers = {
    "Content-Type": "application/json",
    Accept: "text/event-stream, text/plain;q=0.9, */*;q=0.8",
    tenantId, // <-- header only
  };

  const body = JSON.stringify({ message }); // <-- body is  { message }

  const res = await fetchWithAuthRetry(url, {
    method: "POST",
    credentials: "include",
    headers,
    body,
    signal,
  });
  if (!res.ok || !res.body) {
    const err = new Error(`Failed to stream response: ${res.status}`);
    console.error(err);
    throw err;
  }

  const contentType = (res.headers.get("content-type") || "").toLowerCase();
  const isSSE = contentType.includes("text/event-stream");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const sleep = (ms) =>
    ms ? new Promise((r) => setTimeout(r, ms)) : Promise.resolve();
  const emit = async (raw) => {
    if (!raw) return;
    let out = raw;
    if (jsonField) {
      try {
        const obj = JSON.parse(raw);
        if (typeof obj?.[jsonField] === "string") out = obj[jsonField];
      } catch {}
    }
    if (out === "[DONE]" || out === "__END__") return;
    await sleep(delayMs);
    if (typeof onChunk === "function") onChunk(String(out));
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunkText = decoder.decode(value, { stream: true });

      if (isSSE) {
        buffer += chunkText;
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const eventBlock = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);

          const lines = eventBlock.split(/\r?\n/);
          const dataLines = [];
          for (const line of lines) {
            const trimmed = line.trim();
            if (trimmed.startsWith("data:"))
              dataLines.push(trimmed.slice(5).trimStart());
          }
          if (dataLines.length) {
            await emit(dataLines.join("\n"));
          } else if (eventBlock.trim()) {
            await emit(eventBlock.trim());
          }
        }
      } else {
        await emit(chunkText);
      }
    }

    // flush leftover for SSE
    if (isSSE && buffer.trim()) {
      const lines = buffer.trim().split(/\r?\n/);
      const dataLines = [];
      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith("data:"))
          dataLines.push(trimmed.slice(5).trimStart());
      }
      if (dataLines.length) await emit(dataLines.join("\n"));
      else await emit(buffer.trim());
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {}
  }

  return { ok: true };
}

// Stream with FormData (files + context) but same base URL & headers as old flow
export async function streamPromptResponseWithFiles(
  formData,
  { onChunk, signal, delayMs = 0, jsonField = null } = {},
) {
  if (!(formData instanceof FormData)) {
    throw new Error(
      "streamPromptResponseWithFiles: formData (FormData) is required",
    );
  }

  const tenantId = sessionStorage.getItem("tenantId");
  if (!tenantId)
    throw new Error("streamPromptResponseWithFiles: tenantId is required");

  const base = String(apiClient?.defaults?.baseURL || "").replace(/\/+$/, "");
  const url = `${base}/api/v3/ai/stream`;
  console.log("streamPromptResponseWithFiles - URL:", url);

  // Don't set Content-Type; browser sets multipart boundary
  const headers = {
    Accept: "text/event-stream, text/plain;q=0.9, */*;q=0.8",
    tenantId, // header only (same key casing as in old flow)
  };

  const res = await fetchWithAuthRetry(url, {
    method: "POST",
    credentials: "include",
    headers,
    body: formData,
    signal,
  });
  if (!res.ok || !res.body) {
    const err = new Error(`Failed to stream response: ${res.status}`);
    console.error(err);
    throw err;
  }

  const contentType = (res.headers.get("content-type") || "").toLowerCase();
  const isSSE = contentType.includes("text/event-stream");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const sleep = (ms) =>
    ms ? new Promise((r) => setTimeout(r, ms)) : Promise.resolve();
  const emit = async (raw) => {
    if (!raw) return;
    let out = raw;
    if (jsonField) {
      try {
        const obj = JSON.parse(raw);
        if (typeof obj?.[jsonField] === "string") out = obj[jsonField];
      } catch {}
    }
    if (out === "[DONE]" || out === "__END__") return;
    await sleep(delayMs);
    if (typeof onChunk === "function") onChunk(String(out));
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunkText = decoder.decode(value, { stream: true });

      if (isSSE) {
        buffer += chunkText;
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const eventBlock = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);

          const lines = eventBlock.split(/\r?\n/);
          const dataLines = [];
          for (const line of lines) {
            const trimmed = line.trim();
            if (trimmed.startsWith("data:"))
              dataLines.push(trimmed.slice(5).trimStart());
          }
          if (dataLines.length) await emit(dataLines.join("\n"));
          else if (eventBlock.trim()) await emit(eventBlock.trim());
        }
      } else {
        await emit(chunkText);
      }
    }

    // flush leftover
    if (isSSE && buffer.trim()) {
      const lines = buffer.trim().split(/\r?\n/);
      const dataLines = [];
      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith("data:"))
          dataLines.push(trimmed.slice(5).trimStart());
      }
      if (dataLines.length) await emit(dataLines.join("\n"));
      else await emit(buffer.trim());
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {}
  }

  return { ok: true };
}

// GET All sessions for a tenant
export const getAllChatSessions = async () => {
  try {
    const response = await apiClient.post("/api/v3/ai/list-sessions", {
      userId: sessionStorage.getItem("_id") || "",
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching chat sessions:", error);
    throw error;
  }
};

// GET HISTORY of a session by ID
export const getChatSessionHistory = async (data) => {
  try {
    return await postJsonWithReferrer("/api/v3/ai/get-messages", data, {
      referrerPolicy: "strict-origin-when-cross-origin",
    });
  } catch (error) {
    console.error("Error fetching chat session history:", error);
    throw error;
  }
};

export const saveChatSession = async (
  sessionId,
  messages,
  pastSessionId = null,
) => {
  try {
    const userId = sessionStorage.getItem("_id") || "";
    const payload = pastSessionId
      ? {
          session_id: sessionId,
          past_session_id: pastSessionId,
          messages,
          userId,
        }
      : {
          session_id: sessionId,
          messages,
          userId,
        };

    return await postJsonWithReferrer("/api/v3/ai/save-chat", payload, {
      referrerPolicy: "strict-origin-when-cross-origin",
    });
  } catch (error) {
    console.error("Error saving chat session:", error);
    throw error;
  }
};

export const deleteChatSession = async (sessionId) => {
  try {
    const response = await apiClient.post("/api/v3/ai/delete-chat", {
      session_id: sessionId,
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting chat session:", error);
    throw error;
  }
};

export const getVisualizationData = async (responseId) => {
  try {
    const response = await fetchWithAuthRetry(
      "https://test-prompt-systems-drgmdtbmb5cvdfgg.eastus-01.azurewebsites.net/api/get-visualization-data",
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          tenantId: sessionStorage.getItem("tenantId") || "",
        },
        body: JSON.stringify({ responseId }),
      },
    );

    if (!response.ok) {
      console.error("Failed to fetch visualization data:", response.status);
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching visualization data:", error);
    return null;
  }
};

// Download Chat PDF helper
export async function downloadChatPdf(sessionId) {
  if (!sessionId) throw new Error("downloadChatPdf: sessionId is required");

  // Build base URL from apiClient like the other APIs
  const base = String(
    typeof apiClient?.defaults?.baseURL === "string"
      ? apiClient.defaults.baseURL
      : "",
  ).replace(/\/+$/, "");
  const url = `${base}/api/v3/ai/download-pdf?session_id=${encodeURIComponent(
    sessionId,
  )}`;

  // The backend requires a lowercase 'tenantid' header (not 'tenantId')
  const tenantEncrypted = sessionStorage.getItem("tenantId") || "";
  const referrer = getRequestReferrer();

  const res = await fetchWithAuthRetry(url, {
    method: "GET",
    credentials: "include",
    headers: {
      tenantid: tenantEncrypted,
    },
    ...(referrer
      ? {
          referrer,
          referrerPolicy: "strict-origin-when-cross-origin",
        }
      : {}),
  });

  if (!res.ok) {
    const status = res.status;
    // Optionally surface friendly messages for common cases:
    if (status === 400) throw new Error("Missing session_id");
    if (status === 401) throw new Error("Unauthorized (JWT invalid/missing)");
    if (status === 403) throw new Error("Forbidden (tenantid invalid)");
    if (status === 404) throw new Error("Session not found");
    throw new Error(`Download failed with status ${status}`);
  }

  // Return blob so the UI layer can control file naming & download
  return await res.blob();
}

const getAuthTokenForUpload = () =>
  sessionStorage.getItem("JWT") ||
  sessionStorage.getItem("accessToken") ||
  sessionStorage.getItem("authToken") ||
  sessionStorage.getItem("token") ||
  "";

const buildCategoryPrefixedFileName = (fileName, category) => {
  const safeName = String(fileName || "").trim();
  const safeCategory = String(category || "").trim();

  if (!safeName || !safeCategory) return safeName;

  return safeName.startsWith(`${safeCategory}_`)
    ? safeName
    : `${safeCategory}_${safeName}`;
};

const fileNameMatchesUploadEntry = (serverFileName, localFileName, category) => {
  const serverName = String(serverFileName || "").trim();
  const localName = String(localFileName || "").trim();
  const prefixedLocalName = buildCategoryPrefixedFileName(localName, category);

  return serverName === localName || serverName === prefixedLocalName;
};

export async function getFileCategoryCounts(
  userId = sessionStorage.getItem("_id"),
) {
  const tenantId = sessionStorage.getItem("tenantId");
  const token = getAuthTokenForUpload();

  if (!userId) throw new Error("getFileCategoryCounts: userId is required");
  if (!tenantId) throw new Error("getFileCategoryCounts: tenantId is required");

  const base = getApiBaseUrl();
  const url = `${base}/api/v3/ai/file-category-counts?userId=${encodeURIComponent(
    userId,
  )}`;

  const response = await fetchWithAuthRetry(url, {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
      tenantid: tenantId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const responseData = await parseResponseBody(response);

  if (!response.ok) {
    const requestError = new Error(
      responseData?.message ||
        `Request failed with status ${response.status}`,
    );
    requestError.status = response.status;
    requestError.response = {
      status: response.status,
      data: responseData,
    };
    throw requestError;
  }

  return {
    status: responseData?.status,
    userId: responseData?.userId || userId,
    categoryCounts: responseData?.categoryCounts || {},
  };
}

// Upload one or more files to blob storage using the batch upload contract.
export async function uploadChatFile(
  files,
  fileType,
  { replaceExisting = false, userId: providedUserId } = {},
) {
  const tenantId = sessionStorage.getItem("tenantId");
  const userId = providedUserId || sessionStorage.getItem("_id");

  if (!tenantId) throw new Error("uploadChatFile: tenantId is required");
  if (!userId) throw new Error("uploadChatFile: userId is required");

  const fileList = Array.isArray(files) ? files : [files];
  const validFiles = fileList.filter((file) => file instanceof File);

  if (!validFiles.length) {
    throw new Error("uploadChatFile: at least one valid file is required");
  }

  const normalizedReplaceExisting =
    replaceExisting === true || String(replaceExisting).toLowerCase() === "true";

  const formData = new FormData();
  formData.append("userId", String(userId));
  formData.append("replaceExisting", String(normalizedReplaceExisting));

  validFiles.forEach((file) => {
    const uploadFileName = buildCategoryPrefixedFileName(file.name, fileType);
    formData.append("files", file, uploadFileName);
  });

  try {
    const token = getAuthTokenForUpload();

    const response = await apiClient.post(
      "/api/v3/ai/upload-chat-file",
      formData,
      {
        headers: {
          "Content-Type": undefined,
          tenantid: tenantId,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    );

    const responseData = response?.data || {};
    const uploadedFiles = Array.isArray(responseData.uploadedFiles)
      ? responseData.uploadedFiles
      : [];
    const skippedFiles = Array.isArray(responseData.skippedFiles)
      ? responseData.skippedFiles
      : [];
    const failedFiles = Array.isArray(responseData.failedFiles)
      ? responseData.failedFiles
      : [];

    const normalizedUploadedFiles = uploadedFiles.map((item) => {
      const fileName = String(item?.fileName || item?.name || "");
      const sourceFile = validFiles.find((file) =>
        fileNameMatchesUploadEntry(fileName, file.name, fileType),
      );

      return {
        fileName,
        name: sourceFile?.name || fileName,
        url: item?.url || "",
        type: fileType,
        size: sourceFile?.size || 0,
      };
    });

    return {
      status: responseData?.status,
      message: responseData?.message || "",
      summary: responseData?.summary || {
        totalReceived: validFiles.length,
        uploaded: normalizedUploadedFiles.length,
        replaced: 0,
        skipped: skippedFiles.length,
        failed: failedFiles.length,
      },
      uploadedFiles: normalizedUploadedFiles,
      skippedFiles,
      failedFiles,
      replaceExisting: normalizedReplaceExisting,
    };
  } catch (error) {
    console.error("File upload error:", error);
    throw error;
  }
}

// List all uploaded chat files for the current user
export async function listUserChatFiles(
  userId = sessionStorage.getItem("_id"),
) {
  if (!userId) throw new Error("listUserChatFiles: userId is required");

  const tenantId = sessionStorage.getItem("tenantId");

  try {
    const response = await apiClient.get("/api/v3/ai/list-chat-files", {
      params: { userId },
      headers: {
        ...(tenantId ? { tenantId } : {}),
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error listing chat files:", error);
    throw error;
  }
}

// Delete a chat file for the current user
export async function deleteChatFile(
  blobName,
  userId = sessionStorage.getItem("_id"),
) {
  if (!userId) throw new Error("deleteChatFile: userId is required");
  if (!blobName) throw new Error("deleteChatFile: blobName is required");

  const tenantId = sessionStorage.getItem("tenantId");

  try {
    const response = await apiClient.post(
      "/api/v3/ai/delete-chat-file",
      {
        userId,
        blobName,
      },
      {
        headers: {
          ...(tenantId ? { tenantId } : {}),
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error("Error deleting chat file:", error);
    throw error;
  }
}

// Get a chat file from blob storage
export async function getChatFile(
  blobName,
  userId = sessionStorage.getItem("_id"),
) {
  if (!userId) throw new Error("getChatFile: userId is required");
  if (!blobName) throw new Error("getChatFile: blobName is required");

  const tenantId = sessionStorage.getItem("tenantId");

  const base = String(apiClient?.defaults?.baseURL || "").replace(/\/+$/, "");
  const params = new URLSearchParams({
    blobName,
    userId,
  });
  const url = `${base}/api/v3/ai/get-chat-file?${params}`;

  const res = await fetchWithAuthRetry(url, {
    method: "GET",
    credentials: "include",
    cache: "no-store",
    headers: {
      ...(tenantId ? { tenantId } : {}),
      "Cache-Control": "no-cache, no-store, max-age=0",
      Pragma: "no-cache",
    },
  });

  if (!res.ok) {
    throw new Error(`getChatFile failed with status ${res.status}`);
  }

  const contentType = (res.headers.get("content-type") || "").toLowerCase();

  if (contentType.includes("application/json")) {
    const data = await res.json();
    const downloadUrl = data?.url || data?.fileUrl || data?.downloadUrl;
    if (!downloadUrl) {
      throw new Error("getChatFile returned JSON without a download URL");
    }

    const fileRes = await fetch(downloadUrl, {
      method: "GET",
      credentials: "include",
      cache: "no-store",
    });
    if (!fileRes.ok) {
      throw new Error(
        `getChatFile URL download failed with status ${fileRes.status}`,
      );
    }

    const fileBlob = await fileRes.blob();
    if (!fileBlob.size) {
      throw new Error("Downloaded file is empty");
    }

    return new File([fileBlob], blobName, {
      type: fileBlob.type || "application/octet-stream",
    });
  }

  if (contentType.startsWith("text/")) {
    // Read from a clone so CSV/text file bodies can still be consumed as a blob below.
    const textResponse = (await res.clone().text()).trim();
    if (/^https?:\/\//i.test(textResponse)) {
      const fileRes = await fetch(textResponse, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
      });
      if (!fileRes.ok) {
        throw new Error(
          `getChatFile URL download failed with status ${fileRes.status}`,
        );
      }

      const fileBlob = await fileRes.blob();
      if (!fileBlob.size) {
        throw new Error("Downloaded file is empty");
      }

      return new File([fileBlob], blobName, {
        type: fileBlob.type || "application/octet-stream",
      });
    }
  }

  const blob = await res.blob();
  if (!blob.size) {
    throw new Error("getChatFile returned an empty file response");
  }

  return new File([blob], blobName, {
    type: blob.type || "application/octet-stream",
  });
}
